import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Inbox, Database, Search, Plane, Share2, ListChecks, UserCog, Users, BookOpen, Lock, Briefcase, Shield, CheckCircle2 } from "lucide-react";
import { SectionLabel, HighlightText, ToolPill, SectionHeading } from "@/components/Bits";
import ComparisonTable from "@/components/ComparisonTable";
import IndustriesGrid from "@/components/IndustriesGrid";
import TeamCarousel from "@/components/TeamCarousel";
import FAQAccordion from "@/components/FAQAccordion";
import FinalCTA from "@/components/FinalCTA";
import { INDUSTRIES_VA } from "@/lib/content";

const VA_SERVICES = [
  { icon: Calendar, title: "Calendar & Schedule Management", desc: "Meetings scheduled, conflicts handled, reminders managed, and your day kept organized." },
  { icon: Inbox, title: "Inbox Management", desc: "Emails sorted, flagged, drafted, and followed up so important messages do not get buried." },
  { icon: Database, title: "Data Entry & CRM Management", desc: "Clean and accurate records maintained across your CRM, spreadsheets, and internal systems." },
  { icon: Search, title: "Research & Reporting", desc: "Market research, competitor notes, vendor lists, and business reports prepared for review." },
  { icon: Plane, title: "Travel Coordination", desc: "Flights, hotels, transfers, itineraries, and changes handled with clear communication." },
  { icon: Share2, title: "Social Media Assistance", desc: "Post scheduling, basic engagement, content coordination, and profile admin support." },
  { icon: ListChecks, title: "Project Coordination", desc: "Tasks tracked, deadlines followed up, and team updates organized so work keeps moving." },
  { icon: UserCog, title: "Personal Admin Support", desc: "Appointments, reminders, bookings, and routine personal admin handled with care and discretion." },
];

const EA_SERVICES = [
  { icon: Users, title: "CXO & Founder Support", desc: "Reliable support for founders, CEOs, CFOs, COOs and senior leaders who need someone they can trust with their day-to-day flow." },
  { icon: BookOpen, title: "Board Meeting Preparation", desc: "Agendas, minutes, presentations, reports and pre-read materials prepared and shared on time." },
  { icon: Briefcase, title: "Investor & Stakeholder Comms", desc: "Drafts, follow-ups, updates, and communication support handled professionally." },
  { icon: Calendar, title: "Strategic Scheduling", desc: "Your calendar managed with context, not just availability. The right meetings get the right space." },
  { icon: Lock, title: "Confidential Document Management", desc: "Sensitive documents organized, tracked, and handled with proper access and discretion." },
  { icon: Shield, title: "Vendor & Partner Liaison", desc: "External follow-ups managed professionally so your business is represented well." },
  { icon: ListChecks, title: "Team & Operations Oversight", desc: "Internal coordination, team reminders, status follow-ups, and operational admin handled so leadership can stay focused." },
  { icon: ListChecks, title: "Plus all the tasks a VA does", desc: "From scheduling and CRM updates to daily admin, your EA handles it all, providing executive and operational support."},
];

const TOOLS = [
  { cat: "Communication & Collaboration", items: ["Microsoft Teams", "Slack", "Zoom", "Google Meet"] },
  { cat: "Calendar & Scheduling", items: ["Outlook", "Motion", "Calendly", "Google Calendar"] },
  { cat: "Project Management", items: ["Asana", "Notion", "ClickUp", "Trello", "Monday.com"] },
  { cat: "CRM & Sales Tools", items: ["Salesforce", "Zoho", "Pipedrive"] },
  { cat: "Document & File Management", items: ["Microsoft Office", "Google Workspace", "Dropbox", "Notion"] },
  { cat: "Travel & Expense", items: ["TripIt", "Ramp", "Brex", "Expensify"] },
  { cat: "Social Media", items: ["Hootsuite", "Later", "LinkedIn", "Buffer"] },
];

const TEAM = [
  { initials: "DK", name: "Divya K.", role: "VA · 5 yrs", bio: "Board level support and investor communications.", tools: ["Salesforce", "Zoom", "Notion", "Trello"] },
  { initials: "RS", name: "Rohit S.", role: "EA · 7 yrs", bio: "C-suite calendar, travel and confidential comms.", tools: ["Outlook", "Motion", "Slack", "Notion"] },
  { initials: "PA", name: "Priya A.", role: "VA · 4 yrs", bio: "Project coordination and CRM hygiene for founders.", tools: ["ClickUp", "HubSpot", "Asana", "Slack"] },
  { initials: "VG", name: "Vikram G.", role: "EA · 6 yrs", bio: "Strategic scheduling and board pack preparation.", tools: ["Google Workspace", "Calendly", "Notion", "Zoom"] },
  { initials: "NM", name: "Neha M.", role: "VA · 5 yrs", bio: "Inbox triage, research and meeting prep.", tools: ["Gmail", "Notion", "Loom", "Trello"] },
  { initials: "SK", name: "Sahil K.", role: "EA · 8 yrs", bio: "Operations oversight and vendor liaison.", tools: ["Salesforce", "Slack", "Zoom", "Asana"] },
];

const FAQ = [
  { q: "What is the difference between a VA and an EA?", a: "VAs handle operational and admin tasks (inbox, scheduling, CRM, research). EAs work directly with executives on strategic scheduling, board prep, confidential comms and stakeholder management." },
  { q: "How quickly can I get matched with an assistant?", a: "Most clients receive a first match within 2 hours and a fully briefed assistant in their workflow within 24-48 hours." },
  { q: "Can my EA handle confidential executive communications?", a: "Yes. EAs sign NDAs, work from secure managed offices, and follow your access protocols for sensitive comms and documents." },
  { q: "What if I need both a VA and an EA?", a: "We frequently staff blended teams — one EA for executive support and one or more VAs for operational coverage. One US partner manages the relationship." },
  { q: "Do I need to train them on how we work?", a: "We absorb that for you. Before day one, we map your workflows, brand voice and tools so your assistant is briefed and operational from hour one." },
  { q: "What about data privacy and security?", a: "Managed-office only, NDA-signed, audit trails on request, SSO/role-based access supported, and US partner accountability for every action." },
];

export default function VirtualAssistance() {
  return (
    <>
      <Helmet>
        <title>Hire Professional Virtual Assistant Services for Your Business | Aidify</title>
        <meta name="description" content="Looking for reliable Virtual Assistant Services? Aidify offers Online Virtual Assistant Services, Professional Virtual Assistant Services to streamline your business operations. Get Started Today!" />
      </Helmet>
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 pt-10 lg:pt-16">
        <SectionLabel>VA / EA Professionals</SectionLabel>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mt-6 items-end">
          <div className="lg:col-span-8" data-reveal>
            <h1 className="font-display font-extrabold tracking-tight text-forest-600 text-5xl md:text-7xl lg:text-[80px] leading-[0.95]">
              Reclaim Your Time.
              <br />
              <span className="font-serif-italic font-normal">Multiply Your </span>
              <HighlightText><span className="font-serif-italic font-normal">Impact.</span></HighlightText>
            </h1>
            <p className="mt-7 max-w-xl text-forest-400 text-[16px] leading-relaxed">
              Get a dedicated Virtual Assistant or Executive Assistant backed by a named US partner who ensures consistent performance every day. No more managing freelancers or dealing with unreliable support.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to="/contact" className="btn-primary">Find Your Assistant Today <ArrowRight size={16} /></Link>
              <span className="text-xs text-forest-400/80">Available within 2 hours · Managed support · Trained Professionals</span>
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="aidify-card-dark p-5" data-reveal>
              <div className="text-xs uppercase tracking-[0.18em] text-cream-100/60 mb-4">Live Snapshot</div>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-forest-500/40 rounded-lg p-3"><div className="font-display font-extrabold text-2xl text-cream-100">2h</div><div className="text-[10px] uppercase tracking-wider text-cream-100/60">avg. match</div></div>
                <div className="bg-forest-500/40 rounded-lg p-3"><div className="font-display font-extrabold text-2xl text-cream-100">100%</div><div className="text-[10px] uppercase tracking-wider text-cream-100/60">US Oversight</div></div>
                <div className="bg-forest-500/40 rounded-lg p-3"><div className="font-display font-extrabold text-2xl text-cream-100">8+</div><div className="text-[10px] uppercase tracking-wider text-cream-100/60">Yrs avg exp</div></div>
                <div className="bg-forest-500/40 rounded-lg p-3"><div className="font-display font-extrabold text-2xl text-cream-100">15+</div><div className="text-[10px] uppercase tracking-wider text-cream-100/60">Industries</div></div>
              </div>
              <div className="mt-4 text-xs text-cream-100/60">★★★★★ Bench ready · Pre-vetted</div>
            </div>
          </div>
        </div>

        <div className="mt-14 border-y border-forest-500/10 py-5 overflow-hidden">
          <div className="flex items-center gap-8 whitespace-nowrap">
            <div className="flex gap-10 marquee-track">
              {[...["CXO Support", "Executive Admin", "Calendar Management", "Research", "Inbox Control", "Travel", "Operations Sup"], ...["CXO Support", "Executive Admin", "Calendar Management", "Research", "Inbox Control", "Travel", "Operations Sup"]].map((t, i) => (
                <span key={i} className="font-display font-bold text-2xl text-forest-400/40 uppercase tracking-wider">{t}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="What they do"
          title="Admin, Operations, and Executive Support"
          italic="all under one roof."
          description="Whether you need someone to handle your inbox or support your entire executive team, our assistants operate at the level your business demands."
        />
        

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {VA_SERVICES.map((s, i) => (
            <div key={i} className={`aidify-card p-5 ${i === 1 ? "aidify-card-sage" : ""}`} data-reveal>
              <div className="w-9 h-9 rounded-lg bg-sage-200 flex items-center justify-center mb-4">
                <s.icon size={16} className="text-forest-600" />
              </div>
              <h4 className="font-display font-bold text-forest-600 text-base">{s.title}</h4>
              <p className="text-xs text-forest-400 mt-2 leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 text-xs uppercase tracking-[0.2em] text-forest-400 mb-3">Executive Assistant Responsibilities</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {EA_SERVICES.map((s, i) => (
            <div key={i} className={`aidify-card p-5 ${i < 3 ? "aidify-card-dark" : ""}`} data-reveal>
              <div className={`w-9 h-9 rounded-lg ${i < 3 ? "bg-forest-500/50" : "bg-sage-200"} flex items-center justify-center mb-4`}>
                <s.icon size={16} className={i < 3 ? "text-sage-200" : "text-forest-600"} />
              </div>
              <h4 className={`font-display font-bold text-base ${i < 3 ? "text-cream-100" : "text-forest-600"}`}>{s.title}</h4>
              <p className={`text-xs mt-2 leading-relaxed ${i < 3 ? "text-cream-100/70" : "text-forest-400"}`}>{s.desc}</p>
            </div>
          ))}
        </div>

        <div className="mt-6 aidify-card-sage p-5 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
          <p className="font-display font-bold text-forest-600 text-lg">
            Good support should make your day easier. <span className="font-serif-italic font-normal tracking-wide">Not create another task to manage.</span>
          </p>
          <Link to="/contact" className="btn-primary text-sm">Talk to Us <ArrowRight size={14} /></Link>
        </div>
      </section>

      {/* TOOLS */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="Tools they work with"
          title="Your Tools. Your Process."
          italic="Our Support."
          description="Our assistants are trained on all prominent business tools and can adapt to your existing workflow after a structured onboarding process."
        />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {TOOLS.map((cat, i) => (
            <div key={i} className="aidify-card p-5" data-reveal>
              <div className="text-xs uppercase tracking-[0.18em] text-forest-400 mb-3">{cat.cat}</div>
              <div className="flex flex-wrap gap-1.5">{cat.items.map((t, j) => <ToolPill key={j}>{t}</ToolPill>)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="Industries we serve"
          title="Assistants that understand your work,"
          italic="not just your task list."
          description="We match assistants based on your process, industry, tools, and communication style. That helps them be productive in one day one and support your team with better context."
        />
        <IndustriesGrid featured={INDUSTRIES_VA.featured} others={INDUSTRIES_VA.others} />
      </section>

      {/* COMPARISON */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="The honest comparison"
          title="More structure than a freelancer."
          italic="Less burden than an in-house hire."
          description="The real issue is not finding someone to do admin work. The issue is finding someone reliable, trained, supervised, and accountable."
        />
        <ComparisonTable />
      </section>

      {/* TEAM */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="Meet your assistants"
          title="Professionals who make"
          italic="great leaders even better."
          description="Every Aidify assistant is vetted, trained and employed as part of our team. They work from a managed office, follow defined processes and are supported by US managers who keep quality consistent."
        />
        <TeamCarousel members={TEAM} />
      </section>

      {/* FAQ */}
      <section className="max-w-[1320px] mx-auto px-5 lg:px-10 mt-24">
        <SectionHeading
          label="Frequently asked"
          title="Questions worth asking"
          italic="before you delegate."
          description="Did not find what you are looking for? Talk with our team and we will walk you through how Aidify works for your situation."
        />
        <FAQAccordion items={FAQ} />
      </section>

      <FinalCTA />
    </>
  );
}
